/**
 * SPEC §9.4 — colour is a map colouring: every location is assigned a slot from
 * a small palette of well-separated OKLCH colours such that no two locations
 * that are geographic neighbours share a slot, and the slot chosen is the one
 * furthest in OKLab from the neighbours already coloured. Adjacent catchments
 * therefore contrast; only locations far apart ever share a colour.
 * The server never sends colours; this runs in the browser.
 */

import { haversineKm } from './geo.ts';

export type Theme = 'dark' | 'light';

/** Lightness band centre per theme (§9.8): cells must stay legible on pale land too. */
const L_CENTRE: Record<Theme, number> = { dark: 0.62, light: 0.52 };
/** Two lightness tiers either side of the centre, so neighbours can differ in value as well as hue. */
const L_SPREAD = 0.09;
const C_TARGET = 0.17;
const HUE_STEP = 45;
const HUES_PER_TIER = 360 / HUE_STEP;
/** How many nearest locations count as neighbours that must not share a colour. */
export const NEIGHBOUR_COUNT = 8;

export interface OKLCH { L: number; C: number; H: number }
export interface OKLab { L: number; a: number; b: number }
export type RGBA = [number, number, number, number];

export interface Site { code: string; lat: number; lon: number }

/**
 * Sixteen slots: eight hues 45° apart on a darker tier, the same eight offset
 * by 22.5° on a lighter tier. Chroma is clamped to what sRGB can show at that
 * lightness and hue, so the palette is in gamut by construction.
 */
export function palette(theme: Theme = 'dark'): OKLCH[] {
  const centre = L_CENTRE[theme];
  const out: OKLCH[] = [];
  for (const [tier, offset] of [[-1, 0], [1, HUE_STEP / 2]] as const) {
    const L = centre + tier * L_SPREAD;
    for (let i = 0; i < HUES_PER_TIER; i++) {
      const H = i * HUE_STEP + offset;
      out.push({ L, C: Math.min(C_TARGET, maxChroma(L, H) * 0.98), H });
    }
  }
  return out;
}

/** Symmetric k-nearest-neighbour graph over great-circle distance. */
export function neighbourhood(sites: Site[], k = NEIGHBOUR_COUNT): Map<string, Set<string>> {
  const graph = new Map<string, Set<string>>(sites.map((s) => [s.code, new Set<string>()]));
  for (const a of sites) {
    const nearest = sites
      .filter((b) => b.code !== a.code)
      .map((b) => ({ code: b.code, d: haversineKm(a.lat, a.lon, b.lat, b.lon) }))
      .sort((x, y) => x.d - y.d || (x.code < y.code ? -1 : 1))
      .slice(0, k);
    for (const n of nearest) {
      graph.get(a.code)!.add(n.code);
      graph.get(n.code)!.add(a.code);
    }
  }
  return graph;
}

/**
 * Greedy graph colouring, Welsh–Powell order (highest degree first, then code),
 * so the result depends only on the set of sites and never on input order.
 * Each site takes the palette slot that maximises its minimum OKLab distance to
 * neighbours already coloured; ties fall to a slot rotation seeded by the code,
 * so isolated clusters do not all start on the same colour.
 */
export function assignColours(sites: Site[], theme: Theme = 'dark', k = NEIGHBOUR_COUNT): Map<string, OKLCH> {
  const valid = sites.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));
  const graph = neighbourhood(valid, k);
  const slots = palette(theme);
  const labs = slots.map(toOklab);
  const order = [...valid].sort((a, b) => graph.get(b.code)!.size - graph.get(a.code)!.size || (a.code < b.code ? -1 : 1));
  const chosen = new Map<string, number>();
  for (const site of order) {
    const taken = [...graph.get(site.code)!].map((n) => chosen.get(n)).filter((i): i is number => i !== undefined);
    const start = fnv1a(site.code) % slots.length;
    let best = -1, bestScore = -Infinity;
    for (let step = 0; step < slots.length; step++) {
      const i = (start + step) % slots.length;
      const score = taken.length ? Math.min(...taken.map((t) => oklabDistance(labs[i]!, labs[t]!))) : Infinity;
      if (score > bestScore) { best = i; bestScore = score; }
    }
    chosen.set(site.code, best);
  }
  return new Map([...chosen].map(([code, i]) => [code, slots[i]!]));
}

/** Largest in-gamut chroma for a lightness and hue, by bisection. */
export function maxChroma(L: number, H: number): number {
  let lo = 0, hi = 0.4;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut({ L, C: mid, H })) lo = mid; else hi = mid;
  }
  return lo;
}

/** Support modulates alpha, never hue: one vote is tentative, eight is solid. */
export function supportAlpha(total: number): number {
  return 0.35 + 0.45 * Math.min(1, total / 8);
}

// --- OKLCH → OKLab → sRGB (Björn Ottosson's OKLab matrices) --------------------

export function toOklab({ L, C, H }: OKLCH): OKLab {
  const h = (H * Math.PI) / 180;
  return { L, a: C * Math.cos(h), b: C * Math.sin(h) };
}

/** Euclidean distance in OKLab, a reasonable stand-in for perceptual difference. */
export function oklabDistance(p: OKLab, q: OKLab): number {
  return Math.hypot(p.L - q.L, p.a - q.a, p.b - q.b);
}

function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

function oklabToLinearSrgb(L: number, a: number, b: number): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3, m = m_ ** 3, s = s_ ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

function gamma(x: number): number {
  return x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
}

const EPS = 1e-6;

/** Linear-light sRGB triple, unclamped — used by the gamut test. */
export function oklchToLinear(c: OKLCH): [number, number, number] {
  const { a, b } = toOklab(c);
  return oklabToLinearSrgb(c.L, a, b);
}

export function inGamut(c: OKLCH): boolean {
  return oklchToLinear(c).every((v) => v >= -EPS && v <= 1 + EPS);
}

/** 8-bit sRGB. Chroma is reduced by bisection if the colour is out of gamut. */
export function oklchToRgb(c: OKLCH): [number, number, number] {
  let lin = oklchToLinear(c);
  if (!inGamut(c)) {
    let lo = 0, hi = c.C;
    for (let i = 0; i < 16; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut({ ...c, C: mid })) lo = mid; else hi = mid;
    }
    lin = oklchToLinear({ ...c, C: lo });
  }
  return lin.map((v) => Math.round(255 * gamma(Math.min(1, Math.max(0, v))))) as [number, number, number];
}
